import { useEffect, useState } from "react";
import { Play, Pause, Download, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import { usePlayer } from "@/context/PlayerContext";
import { api, startCheckout } from "@/lib/api";

const VINYL_IMG =
  "https://images.unsplash.com/photo-1648679012203-89de08925111?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx2aW55bCUyMHJlY29yZCUyMG1vY2t1cCUyMGRhcmt8ZW58MHx8fHwxNzg5MzIyNDMwfDA&ixlib=rb-4.1.0&q=85";

export default function Music() {
  const [tracks, setTracks] = useState([]);
  const [buying, setBuying] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const { current, playing, toggle } = usePlayer();

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
    api.get("/releases").then((r) => setUpcoming(r.data.filter((x) => x.date >= new Date().toISOString().slice(0, 10)))).catch(() => {});
  }, []);

  const buy = async (lookupKey, id) => {
    setBuying(id);
    try {
      await startCheckout(lookupKey);
    } catch {
      toast.error("Checkout failed — try again.");
      setBuying(null);
    }
  };

  return (
    <main data-testid="music-page" className="mx-auto max-w-7xl px-6 pb-40 pt-36">
      <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">THE CATALOG</p>
      <h1 data-testid="music-title" className="font-anton leading-[0.9]">
        <MaskedLine className="text-6xl text-white md:text-8xl" delay={0.1}>MUSIC &</MaskedLine>
        <MaskedLine className="text-outline text-6xl md:text-8xl" delay={0.25}>DOWNLOADS</MaskedLine>
      </h1>

      <Reveal className="mt-20 border border-primary/30 bg-card">
        <div className="flex flex-col items-center gap-10 p-8 md:flex-row md:p-14">
          <div className="relative">
            <img
              src={VINYL_IMG}
              alt="Southern Smoke EP vinyl"
              data-testid="ep-cover-image"
              className={`h-48 w-48 rounded-full border-2 border-primary/40 object-cover md:h-64 md:w-64 ${
                current && playing ? "vinyl-spin" : ""
              }`}
            />
            <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <p className="mb-3 font-mono text-xs tracking-[0.4em] text-primary">NEW EP — 5 TRACKS</p>
            <h2 className="font-anton text-4xl text-white md:text-6xl">SOUTHERN SMOKE EP</h2>
            <p className="mt-4 max-w-lg text-white/60">
              The full project. Five tracks of smoke, neon, and Southern bass — delivered as instant digital downloads.
            </p>
            <button
              data-testid="buy-ep-button"
              onClick={() => buy("ep_southernsmoke", "ep")}
              disabled={buying === "ep"}
              className="mt-8 inline-flex items-center gap-3 bg-primary px-8 py-4 font-mono text-sm font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#fff] disabled:opacity-50"
            >
              <ShoppingCart size={16} />
              {buying === "ep" ? "REDIRECTING..." : "BUY THE EP — $6.99"}
            </button>
          </div>
        </div>
      </Reveal>

      {upcoming.length > 0 && (
        <Reveal className="mt-16">
          <div data-testid="upcoming-releases" className="border border-primary/30 bg-black p-8 md:p-10">
            <p className="font-mono text-xs tracking-[0.4em] text-primary">DROPPING NEXT</p>
            <div className="mt-6 divide-y divide-white/10">
              {upcoming.map((u) => (
                <div key={u.id} data-testid={`upcoming-${u.id.slice(0, 8)}`} className="flex flex-wrap items-center gap-4 py-4">
                  <span className="font-mono text-sm text-primary">{u.date}</span>
                  <span className="flex-1 font-anton text-2xl text-white">{u.title}</span>
                  <span className="bg-primary/15 px-3 py-1 font-mono text-[10px] tracking-[0.25em] text-primary">{u.kind}</span>
                  {u.note && <span className="w-full font-mono text-xs text-white/40 md:w-auto">{u.note}</span>}
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}

      <div data-testid="track-list" className="mt-20">
        {tracks.map((t, i) => {
          const isCurrent = current?.id === t.id;
          return (
            <Reveal key={t.id} delay={i * 0.06}>
              <div
                data-testid={`track-row-${t.id}`}
                className={`group flex items-center gap-5 border-b border-white/10 py-6 transition-colors duration-300 hover:bg-white/[0.03] ${
                  isCurrent ? "bg-primary/[0.06]" : ""
                }`}
              >
                <span className="w-10 font-mono text-sm text-white/30">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <button
                  data-testid={`track-play-${t.id}`}
                  onClick={() => toggle(t)}
                  aria-label={`Play ${t.title}`}
                  className="relative h-14 w-14 shrink-0 overflow-hidden border border-white/15"
                >
                  <img src={t.cover} alt={t.title} className="h-full w-full object-cover" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    {isCurrent && playing ? <Pause size={18} /> : <Play size={18} />}
                  </span>
                  {isCurrent && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-primary">
                      {playing ? <Pause size={18} /> : <Play size={18} />}
                    </span>
                  )}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`truncate font-oswald text-lg font-bold tracking-wider ${isCurrent ? "text-primary" : "text-white"}`}>
                    {t.title}
                  </p>
                  <p className="truncate font-mono text-[10px] tracking-[0.2em] text-white/40">{t.album}</p>
                </div>
                <span className="hidden font-mono text-xs text-white/40 md:block">{t.duration}</span>
                {t.free ? (
                  <a
                    href={t.audio}
                    download={`${t.title}.mp3`}
                    data-testid={`track-download-${t.id}`}
                    className="flex items-center gap-2 border border-white/25 px-4 py-2 font-mono text-[10px] tracking-[0.2em] text-white transition-colors duration-300 hover:border-primary hover:text-primary"
                  >
                    <Download size={13} /> FREE DL
                  </a>
                ) : (
                  <button
                    data-testid={`track-buy-${t.id}`}
                    onClick={() => buy(t.lookup_key, t.id)}
                    disabled={buying === t.id}
                    className="flex items-center gap-2 bg-primary px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    <ShoppingCart size={13} />
                    {buying === t.id ? "..." : `$${t.price.toFixed(2)}`}
                  </button>
                )}
              </div>
            </Reveal>
          );
        })}
      </div>

      <p data-testid="music-previews-note" className="mt-8 font-mono text-[10px] tracking-[0.25em] text-white/30">
        PREVIEWS USE DEMO AUDIO — FULL TRACKS SHIP ON RELEASE.
      </p>
    </main>
  );
}
